import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Button } from '@/components/ui/button';

const BalanceChart = ({ data, title, onPeriodChange, showCurrencies = false }) => {
  const [selectedPeriod, setSelectedPeriod] = useState(7);

  const periods = [
    { value: 0.0035, label: '5 мин', days: 0.0035 },
    { value: 1, label: '24ч', days: 1 },
    { value: 7, label: '7 дней', days: 7 },
    { value: 30, label: '30 дней', days: 30 }
  ];

  const handlePeriodChange = (days) => {
    setSelectedPeriod(days);
    if (onPeriodChange) {
      onPeriodChange(days);
    }
  };

  if (!data || data.length === 0) {
    return (
      <div className="bg-[#0a0a0a] border border-gray-800 rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-white font-semibold">{title}</h3>
          <div className="flex gap-2">
            {periods.map((period) => (
              <Button
                key={period.value}
                size="sm"
                variant={selectedPeriod === period.value ? "default" : "outline"}
                onClick={() => handlePeriodChange(period.value)}
                className={
                  selectedPeriod === period.value
                    ? "bg-[#00ff88] text-black hover:bg-[#00cc6f]"
                    : "border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800"
                }
              >
                {period.label}
              </Button>
            ))}
          </div>
        </div>
        <div className="text-center text-gray-400 py-8">
          Недостаточно данных для отображения графика
        </div>
      </div>
    );
  }

  // Format data for recharts
  const chartData = data.map(item => {
    const date = new Date(item.timestamp);
    let timeFormat;
    
    if (selectedPeriod <= 1) {
      timeFormat = date.toLocaleString('ru-RU', { 
        hour: '2-digit', 
        minute: '2-digit'
      });
    } else {
      timeFormat = date.toLocaleString('ru-RU', { 
        month: 'short', 
        day: 'numeric'
      });
    }
    
    const dataPoint = {
      time: timeFormat,
      total: parseFloat((item.total || 0).toFixed(2))
    };
    
    // Add USDT and USDC if showing currencies
    if (showCurrencies) {
      dataPoint.usdt = parseFloat((item.usdt_total || 0).toFixed(2));
      dataPoint.usdc = parseFloat((item.usdc_total || 0).toFixed(2));
    }
    
    return dataPoint;
  });

  return (
    <div className="bg-[#0a0a0a] border border-gray-800 rounded-lg p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-white font-semibold">{title}</h3>
        <div className="flex gap-2">
          {periods.map((period) => (
            <Button
              key={period.value}
              size="sm"
              variant={selectedPeriod === period.value ? "default" : "outline"}
              onClick={() => handlePeriodChange(period.value)}
              className={
                selectedPeriod === period.value
                  ? "bg-[#00ff88] text-black hover:bg-[#00cc6f]"
                  : "border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800"
              }
            >
              {period.label}
            </Button>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
          <XAxis 
            dataKey="time" 
            stroke="#999"
            tick={{ fill: '#999', fontSize: 12 }}
          />
          <YAxis 
            stroke="#999"
            tick={{ fill: '#999', fontSize: 12 }}
            tickFormatter={(value) => `$${value}`}
          />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: '#1a1a1a', 
              border: '1px solid #333',
              borderRadius: '8px'
            }}
            labelStyle={{ color: '#fff' }}
          />
          <Legend wrapperStyle={{ color: '#999' }} />
          
          {showCurrencies ? (
            <>
              <Line 
                type="monotone" 
                dataKey="usdt" 
                stroke="#22c55e" 
                strokeWidth={2}
                dot={selectedPeriod <= 1 ? { r: 3 } : false}
                activeDot={{ r: 5 }}
                name="USDT"
              />
              <Line 
                type="monotone" 
                dataKey="usdc" 
                stroke="#3b82f6" 
                strokeWidth={2}
                dot={selectedPeriod <= 1 ? { r: 3 } : false}
                activeDot={{ r: 5 }}
                name="USDC"
              />
            </>
          ) : (
            <Line 
              type="monotone" 
              dataKey="total" 
              stroke="#00ff88" 
              strokeWidth={2}
              dot={selectedPeriod <= 1 ? { r: 3 } : false}
              activeDot={{ r: 5 }}
              name="Баланс"
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BalanceChart;
